import { useEffect, useState } from "react";
import api, { eur } from "@/lib/api";
import { Carrot, Package, ChefHat, TrendingUp, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

const StatCard = ({ icon: Icon, label, value, sub, testid }) => (
  <Card className="p-6 border-border" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-medium">{label}</p>
        <p className="font-display text-4xl font-extrabold tracking-tight mt-3 tabular">{value}</p>
        {sub && <p className="text-sm text-muted-foreground mt-1">{sub}</p>}
      </div>
      <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
        <Icon className="w-5 h-5 text-primary" />
      </div>
    </div>
  </Card>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([api.get("/stats"), api.get("/recipes"), api.get("/cost-history")])
      .then(([s, r, h]) => {
        setStats(s.data);
        setRecipes(r.data);
        setHistory(
          (h.data || []).map((d) => ({
            ...d,
            label: new Date(d.date).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" }),
          }))
        );
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading)
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="font-display text-4xl font-extrabold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground mt-1">Panoramica del tuo food cost</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard icon={Carrot} label="Ingredienti" value={stats.ingredients} sub="in archivio" testid="stat-ingredients" />
        <StatCard icon={ChefHat} label="Ricette" value={stats.recipes} sub="prodotti a menù" testid="stat-recipes" />
        <StatCard
          icon={TrendingUp}
          label="Food cost medio"
          value={`${stats.avg_food_cost_pct}%`}
          sub="sul prezzo netto"
          testid="stat-foodcost"
        />
      </div>

      {history.length > 0 && (
        <Card className="p-6 border-border" data-testid="cost-history-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display text-2xl font-bold tracking-tight">Andamento food cost</h2>
              <p className="text-sm text-muted-foreground">Media aggiornata ad ogni import fattura</p>
            </div>
            <span className="text-xs font-mono px-2.5 py-1 rounded-full bg-secondary text-muted-foreground">
              {history.length} rilevazioni
            </span>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={history} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" unit="%" />
              <Tooltip
                contentStyle={{
                  background: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: 8,
                  fontSize: 13,
                }}
                formatter={(v) => [`${v}%`, "Food cost medio"]}
              />
              <Line
                type="monotone"
                dataKey="avg_food_cost_pct"
                stroke="hsl(var(--primary))"
                strokeWidth={2.5}
                dot={{ r: 3, fill: "hsl(var(--primary))" }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      )}

      <div>
        <h2 className="font-display text-2xl font-bold tracking-tight mb-4">Ricette recenti</h2>        {recipes.length === 0 ? (
          <Card className="p-12 text-center border-dashed">
            <Package className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">Nessuna ricetta ancora. Inizia creando i tuoi ingredienti.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recipes.slice(0, 6).map((r) => (
              <Card
                key={r.id}
                className="p-5 border-border hover:shadow-md hover:-translate-y-0.5 transition-transform cursor-pointer"
                onClick={() => navigate("/ricette")}
                data-testid={`dash-recipe-${r.id}`}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-display font-bold text-lg truncate">{r.name}</h3>
                  <span
                    className={`text-xs font-mono px-2 py-1 rounded-full ${
                      r.food_cost_pct <= 35
                        ? "bg-primary/10 text-primary"
                        : "bg-destructive/10 text-destructive"
                    }`}
                  >
                    {r.food_cost_pct}%
                  </span>
                </div>
                <div className="mt-4 flex justify-between text-sm">
                  <div>
                    <p className="text-muted-foreground text-xs">Costo/porzione</p>
                    <p className="font-mono font-semibold">{eur(r.cost_per_portion)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-muted-foreground text-xs">Prezzo vendita</p>
                    <p className="font-mono font-semibold">{eur(r.selling_price_gross)}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
